import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { api as apiClient } from '../services/api';
import './GuestReservationListPage.css';

const GuestReservationListPage = () => {
  const { userInfo } = useContext(UserContext);
  const guestUserId = userInfo?.user_id;
  const guestProfileId = userInfo?.guest_profile?.id;
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  // 게스트 예약 목록 가져오기
  useEffect(() => {
    const fetchGuestReservations = async () => {
      try {
        if (!guestProfileId) {
          throw new Error('게스트 프로필 ID를 찾을 수 없습니다.');
        }

        const data = await apiClient.get(`/guest-artists/${guestProfileId}/reservations`);
        setReservations(data.data?.reservations || []);
      } catch (err) {
        console.error('게스트 예약 조회 실패:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (guestUserId) {
      fetchGuestReservations();
    }
  }, [guestProfileId, guestUserId]);

  // 필터링된 예약 목록
  const filteredReservations = reservations.filter(reservation => {
    if (filter === 'all') return true;
    return reservation.status === filter;
  });

  // 상태별 배지 스타일
  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: '확정', className: 'status-confirmed' },
      pending: { label: '대기', className: 'status-pending' },
      cancelled: { label: '취소', className: 'status-cancelled' },
      completed: { label: '완료', className: 'status-completed' }
    };

    const config = statusConfig[status] || { label: status, className: 'status-default' };

    return (
      <span className={`status-badge ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="guest-reservation-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>예약 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guest-reservation-page">
        <div className="error-container">
          <h2>오류가 발생했습니다</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-reservation-page">
      <div className="guest-reservation-header">
        <h1>게스트 아티스트 예약 관리</h1>
        <p>게스트 기간 동안의 예약 정보를 확인할 수 있습니다.</p>
      </div>

      <div className="filter-section">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            전체 ({reservations.length})
          </button>
          <button
            className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`}
            onClick={() => setFilter('confirmed')}
          >
            확정 ({reservations.filter(r => r.status === 'confirmed').length})
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            대기 ({reservations.filter(r => r.status === 'pending').length})
          </button>
          <button
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            완료 ({reservations.filter(r => r.status === 'completed').length})
          </button>
        </div>
      </div>

      <div className="reservations-container">
        {filteredReservations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>예약이 없습니다</h3>
            <p>현재 필터에 해당하는 예약이 없습니다.</p>
          </div>
        ) : (
          <div className="reservations-grid">
            {filteredReservations.map((reservation) => (
              <div key={reservation.id} className="reservation-card">
                <div className="reservation-header">
                  <h3>{reservation.title || '예약 제목 없음'}</h3>
                  {getStatusBadge(reservation.status)}
                </div>

                <div className="reservation-details">
                  <div className="detail-item">
                    <span className="detail-label">고객:</span>
                    <span className="detail-value">{reservation.client_name || 'N/A'}</span>
                  </div>

                  {reservation.client_instagram && (
                    <div className="detail-item">
                      <span className="detail-label">인스타그램:</span>
                      <span className="detail-value">@{reservation.client_instagram}</span>
                    </div>
                  )}

                  <div className="detail-item">
                    <span className="detail-label">예약일:</span>
                    <span className="detail-value">
                      {reservation.start_time ? formatDate(reservation.start_time) : 'N/A'}
                    </span>
                  </div>

                  {reservation.location && (
                    <div className="detail-item">
                      <span className="detail-label">위치:</span>
                      <span className="detail-value">{reservation.location}</span>
                    </div>
                  )}

                  {reservation.memo && (
                    <div className="detail-item">
                      <span className="detail-label">메모:</span>
                      <span className="detail-value memo-text">{reservation.memo}</span>
                    </div>
                  )}
                </div>

                <div className="reservation-actions">
                  <button className="action-btn view-btn">
                    상세보기
                  </button>
                  <button className="action-btn edit-btn">
                    수정
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="guest-reservation-footer">
        <div className="info-box">
          <h4>게스트 예약 관리 안내</h4>
          <ul>
            <li>게스트 기간 내의 예약만 표시됩니다.</li>
            <li>예약 상태에 따라 필터링할 수 있습니다.</li>
            <li>예약 상세 정보를 확인하고 수정할 수 있습니다.</li>
            <li>달력과 연동되어 일정을 관리할 수 있습니다.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GuestReservationListPage;
